import {Component, OnDestroy, OnInit, WritableSignal} from '@angular/core';
import {BaseComponent} from '../../../core/component/base.component';

import {AlertService} from '../../../core/service/alert.service';
import {MessageService} from '../../../core/service/message.service';
import {Router} from '@angular/router';
import {SecurityService} from '../../../core/service/security.service';
import {LoggerService} from '../../../service/logger.service';

import {WorkdayLog} from '../../service/resource/workday-log';
import {WorkdayLogGenerationResource, WorkdayLogService} from '../../service/workday-log.service';
import {Employee} from '../../service/resource/employee';
import {EmployeeService} from '../../service/employee.service';
import {WorkdayLogEntry} from '../../service/resource/workday-log-entry';
import {WorkdayLogFilter} from '../../service/filter/workday-log.filter';
import {LocalDate} from '@js-joda/core';
import {AbsenceService} from '../../service/absence.service';
import {Absence} from '../../service/resource/absence';
import {FileResource} from '../../../model/file';
import {HttpErrorResponse} from '@angular/common/http';
import {finalize} from 'rxjs';

@Component({
    selector: 'app-workday-log',
    templateUrl: './workday-log.component.html',
    standalone: false
})
export class WorkdayLogComponent extends BaseComponent<WorkdayLog> implements OnInit, OnDestroy {

  employees: Employee[] = [];
  absencesByDate?: Absence[] = [];
  from: LocalDate = LocalDate.now();
  to: LocalDate = LocalDate.now();
  employeeId?: number = -1;

  // Selection state for bulk delete
  allSelected = false;
  countSelections = 0;

  fileUploadErrors: ErrorEntry[] = [];
  attachments: FileResource[] = [];
  selectedAttachment?: FileResource;

  constructor(private workdayService: WorkdayLogService,
              private alertService: AlertService,
              protected messageService: MessageService,
              protected loggerService: LoggerService,
              protected router: Router,
              protected employeeService: EmployeeService,
              protected securityService: SecurityService,
              protected absenceService: AbsenceService) {
    super(workdayService, 'WorkdayLog', alertService, router, securityService, messageService, loggerService);
    this.sortBy = ['date', 'employeeId'];
    this.sortOrder = 'ASC';
    this.pageSize = 25;
    this.from = LocalDate.now().withDayOfMonth(1);
    this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
  }

  getModelInstance(): WorkdayLog {
    return new WorkdayLog();
  }

  clearFormErrors(): Map<string, string> {
    return new Map<string, string>();
  }

  protected cleanData() {

    this.absencesByDate = [];

  }

  onModelUpdate() {
    this.absencesByDate = [];
    this.loadAttachments();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.employeeService.getAll(true).subscribe({
        next: (v) => this.employees = v,
        error: (e) => this.processError(e)
      }
    );
  }

  getPageEvent(pageNumber: number, refresh: boolean = false): void {
    const filter = new WorkdayLogFilter();
    filter.fromDate = this.from;
    filter.toDate = this.to;
    if (this.employeeId !== undefined && this.employeeId !== -1) {
      filter.employeeId = this.employeeId;
    }
    this.loading = true;
    this.service.searchByFilter(filter, pageNumber, this.pageSize, this.sortBy, this.sortOrder)
      .subscribe({
        next: value => {
          this.page = value;
          this.loading = false;
          },
        error: err => this.processError(err)
        });
  }

  addEntryEvent(): void {
    const newEntry = new WorkdayLogEntry();
    this.model.workdayLogEntries.push(newEntry);
  }

  removeEntryEvent(index: number): void {
    this.model.workdayLogEntries.splice(index, 1);
  }

  fetchAbsencesEvent(): void {
    if (this.model.date === undefined || this.model.employee === undefined) {
      return;
    }
    let example = new Absence();
    example.date = this.model.date;
    example.employee = this.model.employee;
    this.absenceService.search(example).subscribe({
      next: value => {
        this.absencesByDate = value.content;
      }
    })
  }

  fromDateChanged(): void {
    if (this.from && this.to) {
      if (this.from.isAfter(this.to)) {
        this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
      }
    } else if (this.from && !this.to) {
      this.to = this.from.withDayOfMonth(this.from.lengthOfMonth());
    }
    this.refreshEvent();
  }

  toDateChanged(): void {
    if (this.from && this.to) {
      if (this.to.isBefore(this.from)) {
        this.from = this.to.withDayOfMonth(1);
      }
    } else if (this.to && !this.from) {
      this.from = this.to.withDayOfMonth(1);
    }
    this.refreshEvent();
  }

  // Helper to avoid arrow functions in template expressions (older Angular versions)
  getSelectedEmployee(): Employee | undefined {
    if (this.employeeId === undefined || this.employeeId === -1) {
      return undefined;
    }
    return this.employees?.find((e: Employee) => e.id === this.employeeId);
  }

  generateMissingEvent(): void {
    if (!this.from || !this.to) {
      this.alert.error('Seleccione un rango de fechas válido');
      return;
    }

    const payload: WorkdayLogGenerationResource = {
      startDate: this.from.toString(),
      endDate: this.to.toString(),
      employeeIds: []
    };

    if (this.employeeId !== undefined && this.employeeId !== -1) {
      payload.employeeIds = [this.employeeId];
    } else {
      const ids = new Set<number>();
      this.employees.forEach(w => {
        if (w.id && !w.archived) {
          ids.add(w.id);
        }
      });
      payload.employeeIds = Array.from(ids.values());
    }

    if (!payload.employeeIds || payload.employeeIds.length === 0) {
      this.alert.error('No hay empleados seleccionados para generar registros');
      return;
    }

    this.loading = true;
    this.workdayService
      .generateMissing(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.alert.success('Registros faltantes generados exitosamente');
          this.refreshEvent();
        },
        error: (error) => this.processError(error)
      });
  }

  changeSelection(allSelected: boolean): void {
    this.countSelections = 0;
    this.page.content?.forEach(value => {
      value.selected = allSelected;
      this.countSelections += value.selected ? 1 : 0;
    });
  }

  changeSingleSelection(selected: boolean): void {
    this.countSelections = 0;
    this.allSelected = true;
    this.page.content?.forEach(value => {
      this.allSelected = this.allSelected && !!value.selected;
      this.countSelections += value.selected ? 1 : 0;
    });
  }

  private getSelectedIds(): number[] {
    this.allSelected = false;
    const ids: number[] = [];
    this.page.content?.forEach(value => {
      if (value.selected) {
        this.countSelections--;
        ids.push(value.id ? value.id : 0);
        value.selected = false;
      }
    });
    return ids;
  }

  deleteSelectedEvent(): void {
    const ids = this.getSelectedIds();
    this.removeAllEvent(ids);
  }

  onFileSelected(event: any): void {
    for (const file of event.target.files) {
      if (file) {
        this.uploadAttachments(file);
      }
    }
    event.target.value = '';
  }

  uploadAttachments(file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    this.fileUploadErrors = [];
    if (this.model.id) {
      this.loading = true;
      this.workdayService.uploadAttachments(formData, this.model.id)
        .subscribe(value => {
            this.loading = false;
            this.refreshEvent();
            super.cleanData();
            this.fileUploadErrors = [];
            this.loadAttachments();
            this.alert.success('Registros cargados exitósamente');
          }
          , (error: HttpErrorResponse) => {
            this.loading = false;
            const errorCode = error.error.error as string;
            if (['PARTIALLY_PROCESSED_FILE'].includes(errorCode)) {
              const errors = error.error.errors as ErrorEntry[];
              errors.forEach(value => this.fileUploadErrors.push(value));
            } else {
              this.processError(error);
            }
          });
    }
  }

  onImportFileSelected(event: any): void {
    for (const file of event.target.files) {
      if (file) {
        this.importFile(file);
      }
    }
    event.target.value = '';
  }

  importFile(file: File): void {
    const formData = new FormData();
    formData.append('file', file);
    this.fileUploadErrors = [];
    this.loading = true;
    this.workdayService.import(formData)
      .subscribe(value => {
          this.loading = false;
          this.refreshEvent();
          super.cleanData();
          this.fileUploadErrors = [];
          this.loadAttachments();
          this.alert.success('Bitácora importada exitósamente');
        }
        , (error: HttpErrorResponse) => {
          this.loading = false;
          const errorCode = error.error.error as string;
          if (['PARTIALLY_PROCESSED_FILE'].includes(errorCode)) {
            const errors = error.error.errors as ErrorEntry[];
            errors.forEach(value => this.fileUploadErrors.push(value));
          } else {
            this.processError(error);
          }
        });

  }


  loadAttachments(): void {
    if (this.model.id) {
      this.loading = true;
      this.workdayService.getAttachments(this.model.id)
        .pipe(finalize(() => this.loading = false))
        .subscribe({
          next: (attachments) => this.attachments = attachments,
          error: (error) => this.processError(error)
        });
    }
  }

  removeAttachment(attachmentId?: number): void {
    if (!this.model?.id || !attachmentId) {
      return;
    }
    this.loading = true;
    this.workdayService.deleteAttachment(this.model.id, attachmentId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.alert.success('Comprobante eliminado exitosamente');
          this.loadAttachments();
        },
        error: (error) => this.processError(error)
      });
  }

  downloadAttachment(attachmentId?: number, attachmentName?: string): void {
    // Guard clause: ensure required identifiers and filename exist before proceeding
    const absenceId = this.model?.id;
    const fileName = attachmentName;
    if (!absenceId || !attachmentId || !fileName) {
      return;
    }

    this.loading = true;
    this.workdayService
      .downloadAttachment(absenceId, attachmentId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (blob: Blob) => this.triggerDownload(blob, fileName),
        error: (error) => this.processError(error)
      });
  }

  private triggerDownload(blob: Blob, fileName: string): void {
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    }
  }
}


class ErrorEntry {
  constructor(
    public message: string,
    public line: string
  ) {
  }
}
